<template>
    <div>
        <div v-if="showEditFormPlaceholder || show">
            <pc-label
                v-bind:label="eSignatureStatusHeader"
                v-bind:show-help="showHelp"
                v-bind:help-link="showHelpLinkOnly"
                v-on:help-clicked="emitHelpClicked" />
            <div>
                <slot name="help" />
            </div>
            <hr class="my-2" />
        </div>
        <div v-if="showEditFormPlaceholder">
            <pc-button disabled>
                {{ requestSignaturesButtonLabel }}
            </pc-button>
        </div>
        <div v-else-if="show">
            <pc-loader
                v-bind:loading="requesting"
                v-bind:message="requestWaitMsg">
                <template v-if="allElementsRequested">
                    <pending-envelope-legacy-display v-bind:show-e-signature-status-header="false" />
                </template>
                <template v-else>
                    <div
                        v-for="elementBag in eSigElementBags"
                        v-bind:key="elementBag.element.PublicId">
                        <div class="row gx-3">
                            <span class="fw-bold col-auto">
                                {{ elementBag.element.AuxiliaryModel.TemplateName }}
                            </span>
                            <span
                                v-bind:class="getStatusClass(elementBag)"
                                class="col-auto">
                                {{ getStatusMessage(elementBag) }}
                            </span>
                        </div>
                        <div
                            v-for="signatory in elementBag.element.Signatories"
                            v-bind:key="signatory.Label"
                            class="d-flex ms-2 small text-body-secondary">
                            {{ signatory.Label }} : {{ getSignatoryEmail(signatory) }}
                        </div>
                    </div>
                    <pc-button
                        v-bind:disabled="!allElementsReady || readonly"
                        v-on:click="requestSignatures">
                        {{ requestSignaturesButtonLabel }}
                    </pc-button>
                </template>
            </pc-loader>
        </div>
    </div>
</template>

<script>
    import Vuex from "vuex";
    import base from "./_elementBase";
    import { isModuleRegistered } from "SharedStores/util";
    import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "../ProjectForm/projectFormStore";
    import { EDIT_FORM_NAMESPACE } from "Forms/EditForm/editFormStoreDefinitions";
    import { ESIGNATURE_CANCEL_EXPLANATION_TYPE } from "ESignatures/eSignatureConstants";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/signatureRequest.i18n";
    import cancelSignaturesDialog from "ESignatures/Legacy/cancelSignaturesDialog.vue";
    import cancelResendDialog from "ESignatures/Legacy/cancelResendEnvelopeDialog.vue";
    import pendingEnvelopeLegacyDisplay from "ESignatures/Legacy/pendingEnvelopeLegacyDisplay.vue";

    export default {
        components: { pendingEnvelopeLegacyDisplay },
        extends: base,
        data() {
            return {
                requesting: false,
                requestWaitMsg: null
            };
        },
        computed: {
            show() {
                return this.eSigElementBags && this.eSigElementBags.length > 0;
            },
            eSigElementBags() {
                return this.$store.getters[PROJECT_FORM_TYPES.getters.visibleESignatureElementBags];
            },
            allElementsReady() {
                return this.eSigElementBags.every((bag) => bag.projectElement.AuxiliaryModel.IsReady);
            },
            allElementsRequested() {
                return this.eSigElementBags.every((bag) => bag.projectElement.AuxiliaryModel.SignatureRequested);
            },
            anyElementsComplete() {
                return this.eSigElementBags.some((bag) => bag.projectElement.AuxiliaryModel.HasCompletedESignature);
            },
            anyElementsIncomplete() {
                return this.eSigElementBags.some((bag) => !bag.projectElement.AuxiliaryModel.HasCompletedESignature);
            },
            allSignaturesComplete() {
                return this.eSigElementBags.every((bag) => bag.projectElement.AuxiliaryModel.HasCompletedESignature);
            },
            projectClientDocumentStatuses() {
                return this.classicESignatureDocumentStatuses;
            },
            signatureRequestStatus() {
                return { documentStatuses: this.projectElement.AuxiliaryModel };
            },
            showCancelIncomplete() {
                return this.anyElementsComplete && this.anyElementsIncomplete;
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            showEditFormPlaceholder() {
                return isModuleRegistered(EDIT_FORM_NAMESPACE, this.$store.state);
            },
            ...Vuex.mapState(PROJECT_FORM_NAMESPACE, {
                hasResent: (state) => state.hasResentESignatures
            }),
            ...Vuex.mapGetters({
                classicESignatureDocumentStatuses: PROJECT_FORM_TYPES.getters.classicESignatureDocumentStatuses,
                visibleESignatureElementBags: PROJECT_FORM_TYPES.getters.visibleESignatureElementBags
            }),
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            getStatusMessage(elementBag) {
                const esigStatus = elementBag.projectElement.AuxiliaryModel;
                if (esigStatus.SignatureRequested) {
                    return this.outForSigningStatusMessage;
                }
                if (esigStatus.IsReady) {
                    return this.readyForSignatureStatusMessage;
                }
                return this.notPreviewedStatusMessage;
            },
            getStatusClass(elementBag) {
                const esigStatus = elementBag.projectElement.AuxiliaryModel;
                if (esigStatus.SignatureRequested) {
                    return "text-bubble-info";
                }
                if (esigStatus.IsReady) {
                    return "text-bubble-success";
                }
                return "text-bubble-warning";
            },
            getSignatoryEmail(signatory) {
                const email = this.getProjectValue(signatory.EmailDataFieldId);
                return email || this.missingEmailPrompt;
            },
            async requestSignatures() {
                this.requesting = true;
                this.requestWaitMsg = this.requestingSignaturesWaitMessage;
                try {
                    await this.$store.dispatch(PROJECT_FORM_TYPES.actions.requestSignatures, null, { root: true });
                } finally {
                    this.requesting = false;
                }
            },
            showSignatureOptions() {
                if (this.allSignaturesComplete) {
                    this.showCancelDialog();
                } else {
                    this.showResendDialog();
                }
            },
            showCancelDialog() {
                if (this.readonly) {
                    return;
                }

                // We only show the cancel dialog directly if all signatures are complete, so
                // only show cancel explanation options that make sense for an envelope that's already
                // complete (e.g. no "resending notification email")
                const cancelExplanationOptions = [
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.UpdatingContents,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other
                ];

                this.$app.showDialog({
                    title: this.cancelSignaturesHeader,
                    customComponent: cancelSignaturesDialog,
                    customComponentOptions: {
                        message: this.aboutToCancelMessage,
                        showCancelIncomplete: this.showCancelIncomplete,
                        separateButtonRow: true,
                        showDontCancel: true,
                        cancelExplanationOptions: cancelExplanationOptions
                    },
                    customComponentListeners: {
                        "cancel-incomplete": this.cancelIncompleteDocuments,
                        "cancel-all": this.cancelAllDocuments,
                        "dont-cancel": this.dontCancel
                    }
                });
            },
            cancelAllDocuments(cancelExplanationData) {
                this.cancelDocuments(PROJECT_FORM_TYPES.actions.cancelAllSignatures, cancelExplanationData);
            },
            cancelIncompleteDocuments(cancelExplanationData) {
                this.cancelDocuments(PROJECT_FORM_TYPES.actions.cancelIncompleteSignatures, cancelExplanationData);
            },
            async cancelDocuments(action, cancelExplanationData) {
                if (this.isCancelExplanationValid(cancelExplanationData)) {
                    this.$app.closeDialog();
                    this.requesting = true;
                    this.requestWaitMsg = this.cancellingSignaturesWaitMessage;
                    try {
                        await this.$store.dispatch(action, cancelExplanationData, { root: true });
                    } finally {
                        this.requesting = false;
                    }
                }
            },
            // Validate that the explanation data exists, has a type, and
            // if the type is 'Other' the explanation detail must be provided
            isCancelExplanationValid(cancelExplanationData) {
                return (
                    cancelExplanationData &&
                    cancelExplanationData.explanationType != null &&
                    (cancelExplanationData.explanationType !== ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other ||
                        Boolean(cancelExplanationData.explanationDetail))
                );
            },
            dontCancel() {
                this.$app.closeDialog();
            },
            async showResendDialog() {
                if (this.readonly) {
                    return;
                }

                this.$app.showDialog({
                    title: this.signatureOptionsHeader,
                    customComponent: cancelResendDialog,
                    customComponentOptions: {
                        showCancelIncomplete: this.showCancelIncomplete
                    },
                    customComponentListeners: {
                        "resend-notifications": this.doResendNotifications,
                        "cancel-incomplete": this.cancelIncompleteDocuments,
                        "cancel-all": this.cancelAllDocuments
                    },
                    large: true,
                    buttons: [
                        {
                            text: this.closeBtnLabel,
                            primary: true,
                            result: "cancel"
                        }
                    ]
                });
            },
            async doResendNotifications() {
                this.$app.closeDialog();
                try {
                    this.requesting = true;
                    this.requestWaitMsg = this.resendingSignaturesWaitMessage;
                    await this.$store.dispatch(PROJECT_FORM_TYPES.actions.resendSignatureNotifications, null, {
                        root: true
                    });
                } finally {
                    this.requesting = false;
                }
            }
        }
    };
</script>
